import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  Draft,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { v4 as uuidV4 } from 'uuid';
import { Layout } from 'react-grid-layout';
import { Card, CardType } from 'utils/customTypes';
import { RootState, ThunkApi } from 'state/store';
import {
  createDashboard,
  createDashboardThroughDuplication,
  getDashboardByIdWithRelationships,
  getDashboardByIdWithAllRelationships,
  setCurrentDashboardId,
} from 'state/normalized/dashboard/dashboardSlice';
import { DashboardSubPath } from 'api/normalized/dashboard/dashboardAPI';

/* ============================== STATE SETUP =============================== */

interface PendingState {
  active: boolean;
  dashboardId?: string;
  additions: Array<Card>;
  deletions: string[]; // keep serializable
  updates: Record<string, Partial<Card>>; // keyed by card id or lid
}

interface CardState {
  pending?: PendingState;
}

let saveInFlight = false;

const initialState: CardState = {};

const cardAdapter = createEntityAdapter<Card>();

function saveChangesCondition(
  arg: { dashboardId: string },
  { getState }: { getState: () => RootState }
): boolean {
  if (saveInFlight) return false;
  const cardsState = getState().normalized.dashboardCard as Draft<
    EntityState<Card> & CardState
  >;
  const pending = cardsState.pending;
  if (!pending || pending.dashboardId !== arg.dashboardId) return false;

  return (
    pending.additions.length > 0 ||
    pending.deletions.length > 0 ||
    Object.keys(pending.updates).length > 0
  );
}

const replaceCards = (
  state: Draft<EntityState<Card> & CardState>,
  dashboardId: string,
  cards: Card[]
) => {
  const existingCardIds =
    (cardAdapter
      .getSelectors()
      .selectAll(state)
      .filter((card) => card.dashboardId === dashboardId)
      .map((card) => card.id)
      .filter(Boolean) as EntityId[]) ?? [];

  cardAdapter.removeMany(state, existingCardIds);
  cardAdapter.upsertMany(state, cards);
};

const ensurePending = (
  state: Draft<EntityState<Card> & CardState>,
  dashboardId: string
) => {
  if (!state.pending || state.pending.dashboardId !== dashboardId) {
    state.pending = {
      active: true,
      dashboardId,
      additions: [],
      deletions: [],
      updates: {},
    };
  }
};

/* ============================== SERVER THUNKS ============================= */

export const createCard = createAsyncThunk<Card, { newCard: Card }, ThunkApi>(
  'normalized/dashboardCards/create',
  async ({ newCard }, { extra: { getDashboardApi } }) => {
    return await getDashboardApi().createDashboardCard(newCard);
  }
);

export const deleteCard = createAsyncThunk<
  boolean,
  { cardId: string; dashboardId: string },
  ThunkApi
>(
  'normalized/dashboardCards/delete',
  async ({ cardId, dashboardId }, { extra: { getDashboardApi } }) => {
    return await getDashboardApi().deleteDashboardCard(dashboardId, cardId);
  }
);

export const updateCard = createAsyncThunk<
  Card,
  {
    cardId: string;
    update: Partial<Card> & Required<Pick<Card, 'dashboardId'>>;
  },
  ThunkApi
>(
  'normalized/dashboardCards/update',
  async ({ cardId, update }, { extra: { getDashboardApi } }) => {
    return await getDashboardApi().updateDashboardCard(cardId, update);
  }
);

/* ============================== LOCAL REDUCERS ============================ */

const cardSlice = createSlice({
  name: 'normalized/dashboardCards',
  initialState: cardAdapter.getInitialState(initialState),
  reducers: {
    addCardsLocal: (
      state,
      action: { payload: { dashboardId: string; cardTypes: CardType[] } }
    ) => {
      const { dashboardId, cardTypes } = action.payload;
      ensurePending(state, dashboardId);

      const additions: Card[] = cardTypes.map((ct) => ({
        lid: `local-${uuidV4()}`,
        cardTypeId: ct.id,
        x: 0,
        y: Infinity, // let RGL compact
        width: ct.defaultWidth,
        height: ct.defaultHeight,
        dashboardId,
      }));

      // TODO: Making this a Class that handles mutability properly will
      //  eliminate the risk of something like `state.pending = undefined` to
      //  happen somewhere else in the app.
      state.pending!.additions.push(...additions);
    },

    deleteCardLocal: (
      state,
      action: { payload: { dashboardId: string; idOrLid: string } }
    ) => {
      const { dashboardId, idOrLid } = action.payload;
      ensurePending(state, dashboardId);

      // If new card (by lid), remove from additions and updates
      const idx = state.pending!.additions.findIndex((c) => c.lid === idOrLid);
      if (idx >= 0) {
        state.pending!.additions.splice(idx, 1);
        delete state.pending!.updates[idOrLid];
        return;
      }

      // Otherwise mark existing id for deletion
      if (!state.pending!.deletions.includes(idOrLid)) {
        state.pending!.deletions.push(idOrLid);
      }
      // Optional: clear any pending updates for that id
      delete state.pending!.updates[idOrLid];
    },

    updateCardLayoutLocal: (
      state,
      action: { payload: { dashboardId: string; layout: Layout[] } }
    ) => {
      const { dashboardId, layout } = action.payload;
      ensurePending(state, dashboardId);

      layout.forEach((item) => {
        const current = state.pending!.updates[item.i] || {};
        state.pending!.updates[item.i] = {
          ...current,
          x: item.x,
          y: item.y === Infinity ? 0 : item.y, // sanitize Infinity
          width: item.w,
          height: item.h,
        };
      });
    },

    cancelCardChanges: (state) => {
      // Drop pending session entirely
      state.pending = undefined;
    },

    applySavedCards: (
      state,
      action: { payload: { dashboardId: string; cards: Card[] } }
    ) => {
      const { dashboardId, cards } = action.payload;
      replaceCards(
        state as Draft<EntityState<Card> & CardState>,
        dashboardId,
        cards
      );
      state.pending = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardByIdWithRelationships.fulfilled, (state, action) => {
        if (action.payload.cards) {
          replaceCards(
            state,
            action.payload.dashboard.id,
            action.payload.cards
          );
        }
      })
      .addCase(
        getDashboardByIdWithAllRelationships.fulfilled,
        (state, action) => {
          if (action.payload.cards) {
            replaceCards(
              state,
              action.payload.dashboard.id,
              action.payload.cards
            );
          }
        }
      )
      .addCase(createDashboardThroughDuplication.fulfilled, (state, action) => {
        if (action.payload.cards) {
          replaceCards(
            state,
            action.payload.dashboard.id,
            action.payload.cards
          );
        }
      })
      .addCase(createDashboard.fulfilled, (state, action) => {
        if (action.payload.cards) {
          replaceCards(
            state,
            action.payload.dashboard.id,
            action.payload.cards
          );
        }
      })
      .addCase(createCard.fulfilled, (state, action) => {
        cardAdapter.upsertOne(state, action.payload);
      })
      .addCase(deleteCard.fulfilled, (state, action) => {
        cardAdapter.removeOne(state, action.meta.arg.cardId);
      })
      .addCase(updateCard.fulfilled, (state, action) => {
        cardAdapter.upsertOne(state, action.payload);
      })
      .addCase(setCurrentDashboardId, (state) => {
        state.pending = undefined;
      });
  },
});

/* ============================== SAVE THUNK ================================ */

export const saveDashboardCardChanges = createAsyncThunk<
  void,
  { dashboardId: string },
  ThunkApi
>(
  'normalized/dashboardCards/saveChanges',
  async (
    { dashboardId },
    { getState, dispatch, extra: { getDashboardApi } }
  ) => {
    saveInFlight = true;
    try {
      const sliceState = getState().normalized.dashboardCard as Draft<
        EntityState<Card> & CardState
      >;

      const pendingState = sliceState.pending;
      const hasNoChanges =
        !pendingState ||
        pendingState.dashboardId !== dashboardId ||
        (pendingState.additions.length === 0 &&
          pendingState.deletions.length === 0 &&
          Object.keys(pendingState.updates).length === 0);

      if (hasNoChanges) {
        return;
      }

      // 1) Create new cards
      const createPromises = pendingState.additions.map((pendingCard) => {
        const updatesForLocalKey = pendingState.updates[pendingCard.lid!];
        const mergedCard: Card = {
          ...pendingCard,
          ...(updatesForLocalKey || {}),
        };
        const { id: _ignoreId, lid: _ignoreLid, ...cardToCreate } = mergedCard;
        if ((cardToCreate as any).y === Infinity) (cardToCreate as any).y = 0;
        return getDashboardApi().createDashboardCard(cardToCreate as any);
      });

      // 2) Delete cards
      const deletePromises = pendingState.deletions.map((cardId) =>
        getDashboardApi().deleteDashboardCard(dashboardId, cardId)
      );

      // 3) Update existing cards (exclude local lids and deleted ids)
      const updatePromises = Object.entries(pendingState.updates)
        .filter(
          ([key]) =>
            !key.startsWith('local-') && !pendingState.deletions.includes(key)
        )
        .map(([cardId, updates]) =>
          getDashboardApi().updateDashboardCard(cardId, {
            ...updates,
            dashboardId,
            y: (updates.y as number | undefined) === Infinity ? 0 : updates.y,
          })
        );

      await Promise.all([
        ...createPromises,
        ...deletePromises,
        ...updatePromises,
      ]);

      // 4) Fetch final cards directly (no thunk) and apply in a single reducer action
      const finalData = await getDashboardApi().getByIdWithRelationships(
        dashboardId,
        DashboardSubPath.CARDS
      );
      const finalCards = finalData.cards ?? [];

      dispatch(
        cardSlice.actions.applySavedCards({
          dashboardId,
          cards: finalCards,
        })
      );
    } finally {
      saveInFlight = false;
    }
  },
  { condition: saveChangesCondition }
);

/* =============================== SELECTORS ================================ */

const defaultSelectors = cardAdapter.getSelectors<RootState>(
  (state) => state.normalized.dashboardCard
);

const selectAllCards = () => (state: RootState) => {
  return defaultSelectors.selectAll(state);
};

const selectCardsForDashboard = (dashboardId: string) => {
  return createSelector([selectAllCards()], (cards: Card[]) => {
    return cards.filter((card) => card.dashboardId === dashboardId);
  });
};

const selectPending = (state: RootState) =>
  state.normalized.dashboardCard.pending;

const selectHasUnsavedChanges = (state: RootState) => {
  const pending = selectPending(state);
  if (!pending) return false;
  return (
    pending.additions.length > 0 ||
    pending.deletions.length > 0 ||
    Object.keys(pending.updates).length > 0
  );
};

const selectWorkingCardsForDashboard = (dashboardId: string) =>
  createSelector(
    [selectCardsForDashboard(dashboardId), selectPending],
    (committed, pending): Card[] => {
      if (!pending || pending.dashboardId !== dashboardId) return committed;

      const existing = committed
        .filter((c) => !pending.deletions.includes(c.id!))
        .map((c) => ({ ...c, ...(pending.updates[c.id!] || {}) }));

      const additions = pending.additions.map((c) => ({
        ...c,
        ...(pending.updates[c.lid!] || {}),
      }));

      return [...existing, ...additions];
    }
  );

export const cardSelectors = {
  ...defaultSelectors,
  selectCardsForDashboard,
  selectWorkingCardsForDashboard,
  selectHasUnsavedChanges,
};

/* ================================= EXPORTS ================================= */

export const {
  addCardsLocal,
  deleteCardLocal,
  updateCardLayoutLocal,
  cancelCardChanges,
} = cardSlice.actions;

export default cardSlice.reducer;
